import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { handleTrapKey } from '@/lib/focus-trap';
import type { ReactNode } from 'react';

/**
 * Themed dialogs and toasts, replacing native alert/confirm/prompt.
 *
 * Native dialogs block the whole tab, can't be styled, and on iOS render as
 * system sheets that look like the browser asking rather than b-mail. Call sites
 * change shape only slightly: `if (!confirm(...)) return;` becomes
 * `if (!(await confirm({...}))) return;`.
 */

type ToastKind = 'info' | 'success' | 'error' | 'warn';

interface Toast {
  id: number;
  kind: ToastKind;
  title?: string;
  message: string;
  detail?: string;
  duration?: number;
}

interface ConfirmOpts {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface PromptOpts extends ConfirmOpts {
  label?: string;
  initial?: string;
  placeholder?: string;
}

interface UiApi {
  toast: (t: Omit<Toast, 'id' | 'kind'> & { kind?: ToastKind }) => void;
  confirm: (o: ConfirmOpts) => Promise<boolean>;
  prompt: (o: PromptOpts) => Promise<string | null>;
}

const UiContext = createContext<UiApi | null>(null);

export function useUi(): UiApi {
  const ctx = useContext(UiContext);
  if (!ctx) throw new Error('useUi must be used inside <UiProvider>');
  return ctx;
}

let seq = 0;

export function UiProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [dialog, setDialog] = useState<(PromptOpts & { type: 'confirm' | 'prompt' }) | null>(null);
  const resolver = useRef<((v: unknown) => void) | null>(null);

  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const toast: UiApi['toast'] = useCallback((t) => {
    const id = ++seq;
    const kind = t.kind ?? 'info';
    setToasts((list) => [...list, { ...t, id, kind }]);
    // Errors stay until dismissed — one that disappears after three seconds is
    // one you'll miss.
    if (kind !== 'error') setTimeout(() => dismiss(id), t.duration ?? 4500);
  }, [dismiss]);

  const ask = useCallback(
    (o: PromptOpts, type: 'confirm' | 'prompt') =>
      new Promise<never>((resolve) => {
        resolver.current = resolve as (v: unknown) => void;
        setDialog({ ...o, type });
      }),
    []
  );

  const confirm = useCallback((o: ConfirmOpts) => ask(o, 'confirm') as unknown as Promise<boolean>, [ask]);
  const prompt = useCallback((o: PromptOpts) => ask(o, 'prompt') as unknown as Promise<string | null>, [ask]);

  const close = useCallback((value: unknown) => {
    setDialog(null);
    resolver.current?.(value);
    resolver.current = null;
  }, []);

  return (
    <UiContext.Provider value={{ toast, confirm, prompt }}>
      {children}

      <div className="pointer-events-none fixed bottom-4 right-4 z-[200] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
           role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id}
            className={`pointer-events-auto flex items-start gap-2 rounded-xl border border-bmail-border bg-bmail-surface p-3 text-sm text-bmail-text shadow-xl border-l-4 ${
              t.kind === 'success' ? 'border-l-bmail-success'
                : t.kind === 'error' ? 'border-l-red-500'
                : t.kind === 'warn' ? 'border-l-bmail-gold' : 'border-l-bmail-gold'
            }`}>
            <div className="min-w-0 flex-1">
              {t.title && <strong className="block">{t.title}</strong>}
              <div className="text-bmail-text-secondary">{t.message}</div>
              {t.detail && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs text-bmail-text-secondary">Details</summary>
                  <div className="mt-1 text-xs text-bmail-text-secondary">{t.detail}</div>
                </details>
              )}
            </div>
            <button onClick={() => dismiss(t.id)} aria-label="Dismiss"
              className="shrink-0 px-1 text-bmail-text-secondary hover:text-bmail-text">×</button>
          </div>
        ))}
      </div>

      {dialog && <Dialog {...dialog} onClose={close} />}
    </UiContext.Provider>
  );
}

function Dialog({
  type, title, body, confirmLabel, cancelLabel, danger, label, initial, placeholder, onClose,
}: PromptOpts & { type: 'confirm' | 'prompt'; onClose: (v: unknown) => void }) {
  const [value, setValue] = useState(initial ?? '');
  const panel = useRef<HTMLDivElement>(null);
  const first = useRef<HTMLElement>(null);

  const cancelValue = type === 'prompt' ? null : false;

  useEffect(() => {
    // 🚩 Return focus to whatever had it, so dismissing a dialog puts the caret back where the user
    // was instead of at the top of the document.
    const previous = document.activeElement as HTMLElement | null;
    // Fall back to the panel: a confirm dialog whose `first` ref has not attached yet would
    // otherwise leave focus outside, and the trap's job is to never start outside.
    (first.current ?? panel.current)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(cancelValue); return; }
      // 🔴 Two faults lived here and are fixed in `handleTrapKey`: the old selector did not exclude
      // DISABLED controls (so Tab could target something unfocusable), and `idx <= 0` pulled focus
      // back only for shift-Tab — a forward Tab from outside the panel walked straight into the
      // page behind the dialog. Shared with the image-optimize modal so there is one answer.
      handleTrapKey(e, panel.current);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [onClose, cancelValue]);

  const done = () => onClose(type === 'prompt' ? (value.trim() || null) : true);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(cancelValue); }}>
      {/* 🚩 tabIndex={-1} so the panel itself can take focus. Without it the trap's "nothing
          focusable" fallback is a silent no-op — .focus() on a plain div does nothing. */}
      <div ref={panel} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}
        className="w-full max-w-md rounded-2xl border border-bmail-border bg-bmail-surface p-5 shadow-2xl focus:outline-none">
        <h3 className="mb-1 text-base font-semibold text-bmail-text">{title}</h3>
        {body && <p className="mb-4 text-sm text-bmail-text-secondary">{body}</p>}
        {type === 'prompt' && (
          <>
            {label && <label className="text-xs text-bmail-text-secondary">{label}</label>}
            <input
              ref={first as React.RefObject<HTMLInputElement>}
              value={value} placeholder={placeholder ?? ''} autoFocus
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') done(); }}
              className="mt-1 w-full rounded-lg border border-bmail-border bg-bmail-bg px-3 py-2 text-sm text-bmail-text focus:outline-none focus:ring-2 focus:ring-bmail-gold"
            />
          </>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={() => onClose(cancelValue)}
            className="rounded-lg border border-bmail-border px-3 py-2 text-sm text-bmail-text hover:border-bmail-gold">
            {cancelLabel ?? 'Cancel'}
          </button>
          <button
            ref={type === 'confirm' ? (first as React.RefObject<HTMLButtonElement>) : undefined}
            onClick={done}
            className={`rounded-lg px-3 py-2 text-sm font-semibold ${
              danger ? 'bg-red-600 text-white hover:bg-red-500'
                     : 'bg-bmail-gold text-black hover:opacity-90'
            }`}>
            {confirmLabel ?? (type === 'prompt' ? 'Save' : 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
